/**
 * StakeholderConvictionStateSpine.jsx
 * GRANULLO_TRUST_ROOM_ARCHITECTURE_EXPANSION_V1
 *
 * Shared truth state layer consumed by all conviction surfaces.
 * One continuous conviction room — not multiple calculators.
 *
 * Canonical shared state primitives:
 * - theorem_progression, planned_vs_actual_revenue, milestone_lineage
 * - moat_layer_growth, patent_family_status, node_density_growth
 * - corridor_expansion, valuation_delta, DCF_scenarios
 * - contradiction_flags, stress_test_results, board_actions
 * - partner_dependencies, client_ROI_paths, sovereign_wedge_optionalities
 *
 * Role-native lens system:
 * investor | board_member | strategic_advisor | partner | key_client
 * sovereign_allocator | prospective_acquirer | internal_exec
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_ROLES = [
  'investor', 'board_member', 'strategic_advisor', 'partner',
  'key_client', 'sovereign_allocator', 'prospective_acquirer', 'internal_exec',
];

const DEFAULT_VALUATION_PARAMS = {
  acv: 200000, contract_count: 12, rail_throughput: 50000000,
  bps_rate: 15, auth_volume: 2000000, node_count: 0,
  geography_live: 0, patent_families: 15, retention: 0.92,
  growth_rate: 0.85, sovereign_probability: 0.3, ip_licensing_probability: 0.2,
};

const StakeholderConvictionContext = createContext(null);

export function useConvictionSpine() {
  const ctx = useContext(StakeholderConvictionContext);
  if (!ctx) throw new Error('useConvictionSpine must be used within StakeholderConvictionProvider');
  return ctx;
}

export function StakeholderConvictionProvider({ children }) {
  // Shared truth objects
  const [truthTracking, setTruthTracking] = useState(null);
  const [convictionReplay, setConvictionReplay] = useState(null);
  const [boardObserver, setBoardObserver] = useState(null);
  const [adversarialScenarios, setAdversarialScenarios] = useState(null);
  const [valuationResult, setValuationResult] = useState(null);
  const [valuationParams, setValuationParams] = useState(DEFAULT_VALUATION_PARAMS);

  // Loading and error
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Role lens
  const [activeRole, setActiveRole] = useState('investor');

  // Prevent duplicate fetches
  const hasFetched = useRef(false);

  // Fetch all truth data on mount (single spine load)
  const fetchAllTruth = useCallback(async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    const errs = {};

    const [ttRes, crRes, boRes, adRes] = await Promise.allSettled([
      fetch(`${API}/api/investor-conviction/truth-tracking`).then(r => r.ok ? r.json() : Promise.reject('truth-tracking')),
      fetch(`${API}/api/investor-conviction/conviction-replay`).then(r => r.ok ? r.json() : Promise.reject('conviction-replay')),
      fetch(`${API}/api/investor-conviction/board-observer`).then(r => r.ok ? r.json() : Promise.reject('board-observer')),
      fetch(`${API}/api/investor-conviction/adversarial`).then(r => r.ok ? r.json() : Promise.reject('adversarial')),
    ]);

    if (ttRes.status === 'fulfilled') setTruthTracking(ttRes.value); else errs.truthTracking = ttRes.reason;
    if (crRes.status === 'fulfilled') setConvictionReplay(crRes.value); else errs.convictionReplay = crRes.reason;
    if (boRes.status === 'fulfilled') setBoardObserver(boRes.value); else errs.boardObserver = boRes.reason;
    if (adRes.status === 'fulfilled') setAdversarialScenarios(adRes.value); else errs.adversarial = adRes.reason;

    setErrors(errs);
    setLoading(false);

    // Also compute initial valuation
    computeValuation(DEFAULT_VALUATION_PARAMS);
  }, []);

  useEffect(() => { fetchAllTruth(); }, [fetchAllTruth]);

  // Valuation computation (called when params change)
  const computeValuation = useCallback(async (params) => {
    try {
      const res = await fetch(`${API}/api/investor-conviction/valuation-lab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (res.ok) {
        const data = await res.json();
        setValuationResult(data);
      }
    } catch { /* silent */ }
  }, []);

  const updateValuationParams = useCallback((newParams) => {
    const merged = { ...valuationParams, ...newParams };
    setValuationParams(merged);
    computeValuation(merged);
  }, [valuationParams, computeValuation]);

  // Adversarial simulation
  const simulateAdversarial = useCallback(async (scenarioId = 'all') => {
    try {
      const res = await fetch(`${API}/api/investor-conviction/adversarial/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario_id: scenarioId }),
      });
      if (res.ok) return await res.json();
    } catch { /* silent */ }
    return null;
  }, []);

  // Derived truth objects for export compatibility
  const derivedState = {
    theorem_progression: truthTracking?.quarters?.filter(q => q.milestone_completed !== null) || [],
    planned_vs_actual_revenue: truthTracking?.quarters?.map(q => ({
      quarter: q.quarter, planned: q.planned_arr, actual: q.actual_arr,
    })) || [],
    milestone_lineage: convictionReplay?.milestones || [],
    patent_family_status: truthTracking?.kpis?.find(k => k.id === 'patents') || null,
    node_density_growth: truthTracking?.kpis?.find(k => k.id === 'nodes') || null,
    corridor_expansion: truthTracking?.kpis?.find(k => k.id === 'corridors') || null,
    valuation_delta: valuationResult?.valuation_range || null,
    dcf_scenarios: valuationResult?.frameworks || null,
    contradiction_flags: boardObserver?.unresolved_contradictions || [],
    stress_test_results: adversarialScenarios?.scenarios || [],
    board_actions: boardObserver?.recommended_board_actions || [],
    conviction_resilience: adversarialScenarios?.conviction_resilience || null,
  };

  // Role-aware view adapters — narrative framing per role
  const getRoleNarrative = useCallback((role) => {
    const narratives = {
      investor: {
        truthQuestion: 'ARE WE TRACKING TO THE THEOREM?',
        valuationQuestion: 'IS THIS VALUATION DEFENSIBLE?',
        replayQuestion: 'WHY DID THIS BECOME MORE INVESTABLE?',
        boardQuestion: 'WHAT COULD BREAK THE MULTIPLE?',
        exportTypes: ['investor_memo', 'ic_memo'],
      },
      board_member: {
        truthQuestion: 'ARE MILESTONES BEING HIT ON TIME?',
        valuationQuestion: 'IS THE VALUATION TRAJECTORY SUSTAINABLE?',
        replayQuestion: 'WHAT DECISIONS LED US HERE?',
        boardQuestion: 'WHAT REQUIRES IMMEDIATE BOARD ACTION?',
        exportTypes: ['board_quarterly_pack'],
      },
      strategic_advisor: {
        truthQuestion: 'WHERE ARE THE LEVERAGE OPPORTUNITIES?',
        valuationQuestion: 'WHAT DRIVES THE NEXT MULTIPLE EXPANSION?',
        replayQuestion: 'WHICH INFLECTION POINTS MATTER MOST?',
        boardQuestion: 'WHERE SHOULD WE FOCUS INTRODUCTIONS?',
        exportTypes: ['advisor_action_memo'],
      },
      partner: {
        truthQuestion: 'IS THE INTEGRATION ON TRACK?',
        valuationQuestion: 'WHAT IS THE SHARED REVENUE OPPORTUNITY?',
        replayQuestion: 'WHAT MILESTONES AFFECT OUR LAUNCH?',
        boardQuestion: 'WHAT ARE THE ACTIVATION BLOCKERS?',
        exportTypes: ['partner_activation_dossier'],
      },
      key_client: {
        truthQuestion: 'IS THE ROI THEOREM BEING VALIDATED?',
        valuationQuestion: 'HOW DOES PLATFORM STABILITY AFFECT US?',
        replayQuestion: 'WHAT CAPABILITIES ARE COMING?',
        boardQuestion: 'WHAT ARE THE COMPLIANCE PROOF POINTS?',
        exportTypes: ['client_roi_deck'],
      },
      sovereign_allocator: {
        truthQuestion: 'IS SOVEREIGN DEPLOYMENT VIABLE?',
        valuationQuestion: 'WHAT IS THE CONTRACT OPTIONALITY?',
        replayQuestion: 'WHAT INFRASTRUCTURE PRECEDENTS EXIST?',
        boardQuestion: 'WHAT REGULATORY READINESS EXISTS?',
        exportTypes: ['sovereign_allocator_brief'],
      },
      prospective_acquirer: {
        truthQuestion: 'IS THE MOAT DEFENSIBLE?',
        valuationQuestion: 'WHAT ARE THE STRATEGIC SYNERGY PATHS?',
        replayQuestion: 'WHAT IP ASSETS ARE UNIQUE?',
        boardQuestion: 'WHAT IS THE CARVE-OUT COMPLEXITY?',
        exportTypes: ['acquisition_diligence_pack'],
      },
      internal_exec: {
        truthQuestion: 'ARE WE EXECUTING TO PLAN?',
        valuationQuestion: 'HOW DOES THIS AFFECT FUNDRAISING?',
        replayQuestion: 'WHAT DROVE THE BIGGEST CONVICTION GAINS?',
        boardQuestion: 'WHAT RISKS NEED IMMEDIATE ATTENTION?',
        exportTypes: ['internal_status_report'],
      },
    };
    return narratives[role] || narratives.investor;
  }, []);

  const value = {
    // Raw truth data
    truthTracking,
    convictionReplay,
    boardObserver,
    adversarialScenarios,
    valuationResult,
    valuationParams,

    // Actions
    updateValuationParams,
    simulateAdversarial,
    setActiveRole,

    // Derived
    derivedState,
    activeRole,
    getRoleNarrative,
    roleNarrative: getRoleNarrative(activeRole),

    // Meta
    loading,
    errors,
    supportedRoles: SUPPORTED_ROLES,
  };

  return (
    <StakeholderConvictionContext.Provider value={value}>
      {children}
    </StakeholderConvictionContext.Provider>
  );
}
